// H-5 발주 설정 — 순수 판정·변환 (시험 대상). 문구는 materialOrder.logic.ts 의 ORDER_MESSAGE.settings.
import { ORDER_MESSAGE } from './materialOrder.logic'

export interface OrderSettingServer {
  workTypeCode: string
  /** 서버 행이 없으면 null (D6) */
  orderYn: string | null
  orderLeadDays: number | null
  vendorId: number | null
  /** SIF-1 '자재파트너가 시공' 방식(ORDER_YN 'Y' + CONSTRUCT 'Y'). 없거나 'N'이면 일반 */
  constructByVendorYn?: string | null
}

/** 화면 초안(표시값). orderYn 은 'Y'|'N' 로 정규화. constructByVendor 미지정은 false(일반) */
export interface OrderSettingDraft {
  orderYn: 'Y' | 'N'
  orderLeadDays: number | null
  vendorId: number | null
  /** SIF-1 자재파트너가 시공 여부. 없으면(undefined) false 로 본다 */
  constructByVendor?: boolean
}

export interface OrderSettingPayloadItem {
  workTypeCode: string
  orderYn: 'Y' | 'N'
  /** 별도발주·자재파트너가 시공은 리드타임 필수. 발주없음만 null 가능 */
  orderLeadDays: number | null
  vendorId: number | null
  /** SIF-1 발주·시공 방식 — 'Y'=자재파트너가 시공까지 맡음 */
  constructByVendorYn: 'Y' | 'N'
}

/** SIF-1 발주·시공 방식 — 별도발주(ORDER_YN Y·CONSTRUCT N) / 발주없음(ORDER_YN N) / 자재파트너가 시공(ORDER_YN Y·CONSTRUCT Y) */
export type OrderMode = 'SEPARATE' | 'NONE' | 'VENDOR_CONSTRUCT'

/** 초안 → 현재 방식 */
export function draftMode(d: OrderSettingDraft): OrderMode {
  if (d.constructByVendor) return 'VENDOR_CONSTRUCT'
  return d.orderYn === 'N' ? 'NONE' : 'SEPARATE'
}

/** 방식 선택 반영 — 리드타임·주거래처 값은 보관(다시 별도발주로 돌아오면 복원) */
export function applyMode(d: OrderSettingDraft, mode: OrderMode): OrderSettingDraft {
  switch (mode) {
    case 'SEPARATE':         return { ...d, orderYn: 'Y', constructByVendor: false }
    case 'NONE':             return { ...d, orderYn: 'N', constructByVendor: false }
    case 'VENDOR_CONSTRUCT': return { ...d, orderYn: 'Y', constructByVendor: true }
  }
}


/** 기준값(서버) → 화면 표시값. 행 없음(orderYn=null)은 켜짐으로 표시(D6). 값을 만들어 채우지 않음(리드타임 null 유지) */
export function toDraft(s: OrderSettingServer | undefined): OrderSettingDraft {
  // 자재파트너가 시공은 항상 orderYn 'Y'(발주없음+자재시공 조합은 만들지 않음)
  const base: OrderSettingDraft = {
    orderYn: s?.orderYn === 'N' && s?.constructByVendorYn !== 'Y' ? 'N' : 'Y',
    orderLeadDays: s?.orderLeadDays ?? null,
    vendorId: s?.vendorId ?? null,
  }
  // 일반(false)이면 필드를 생략해 기존 비교·표시 동작을 그대로 둔다
  return s?.constructByVendorYn === 'Y' ? { ...base, constructByVendor: true } : base
}

export function isDraftChanged(base: OrderSettingDraft, draft: OrderSettingDraft): boolean {
  return base.orderYn !== draft.orderYn
    || base.orderLeadDays !== draft.orderLeadDays
    || base.vendorId !== draft.vendorId
    || !!base.constructByVendor !== !!draft.constructByVendor
}

/** 바꾼 공종 코드 목록(기준값 대비 표시값이 다른 공종) */
export function diffOrderSettings(
  baseDrafts: Map<string, OrderSettingDraft>,
  drafts: Map<string, OrderSettingDraft>,
): string[] {
  const out: string[] = []
  drafts.forEach((d, code) => {
    const b = baseDrafts.get(code)
    if (b && isDraftChanged(b, d)) out.push(code)
  })
  return out
}

/**
 * 저장 요청 구성 — 이 포함 규칙은 이 함수 한 곳에 격리(D6 뒤집기 용이).
 * 포함: 바꾼 공종 + 서버 행이 없는(orderYn=null) 공종의 화면 표시값.
 * 제외: 리드타임이 null 인 공종(7일 등 대입 금지) — skipped 로 센다.
 * 바꾼 공종이 없으면 아무것도 보내지 않는다.
 */
export function buildSettingsPayload(
  order: string[],
  serverRows: Map<string, OrderSettingServer>,
  drafts: Map<string, OrderSettingDraft>,
  changedCodes: string[],
): { items: OrderSettingPayloadItem[]; skipped: number } {
  if (changedCodes.length === 0) return { items: [], skipped: 0 }
  const changed = new Set(changedCodes)
  const items: OrderSettingPayloadItem[] = []
  let skipped = 0
  for (const code of order) {
    const d = drafts.get(code)
    if (!d) continue
    const noRow = (serverRows.get(code)?.orderYn ?? null) === null
    if (!changed.has(code) && !noRow) continue
    // 리드타임은 '별도발주'·'자재파트너가 시공'에서 필수(7일 등 대입 금지) — 발주없음만 리드타임 없이 저장한다
    const mode = draftMode(d)
    if (mode !== 'NONE' && d.orderLeadDays == null) { skipped++; continue }
    items.push({
      workTypeCode: code,
      orderYn: mode === 'NONE' ? 'N' : 'Y',
      orderLeadDays: d.orderLeadDays,
      vendorId: d.vendorId,
      constructByVendorYn: mode === 'VENDOR_CONSTRUCT' ? 'Y' : 'N',
    })
  }
  return { items, skipped }
}

export interface VendorMetricSource {
  avgRating?: number | null
  orderCount?: number | null
  /** SV2 지표 — 값이 있을 때만 표시(null=분모 0 → 그 조각 생략, 0 으로 채우지 않음) */
  replyWithinTwoHoursPercent?: number | null
  onTimePercent?: number | null
  liftAvailPercent?: number | null
}

/** SV2 지표 행 → 후보·옵션 지표 필드. 지표가 없으면 빈 객체(조각 생략) */
export function metricFieldsOf(m: { replyWithinTwoHoursPercent: number | null; onTimeDeliveryPercent: number | null; liftAvailPercent: number | null } | undefined | null): Pick<VendorMetricSource, 'replyWithinTwoHoursPercent' | 'onTimePercent' | 'liftAvailPercent'> {
  if (!m) return {}
  return { replyWithinTwoHoursPercent: m.replyWithinTwoHoursPercent, onTimePercent: m.onTimeDeliveryPercent, liftAvailPercent: m.liftAvailPercent }
}

/** 후보 옵션 둘째 줄 조각 — 값이 없는 조각은 생략, 전부 없으면 이력 없음 문구(0%·임의값 금지) */
export function vendorMetricParts(v: VendorMetricSource): { rating: string | null; rest: string[]; empty: boolean } {
  const rating = v.avgRating != null && v.avgRating > 0 ? v.avgRating.toFixed(1) : null
  const count = v.orderCount ?? null
  const rest: string[] = []
  if (count != null && (count > 0 || rating != null)) rest.push(`거래 ${count}회`)
  // 940 지표 한 줄 순서: 발주회신 2시간 내 → 납기준수 → 양중가능
  if (v.replyWithinTwoHoursPercent != null) rest.push(`발주회신 2시간 내 ${v.replyWithinTwoHoursPercent}%`)
  if (v.onTimePercent != null) rest.push(`납기준수 ${v.onTimePercent}%`)
  if (v.liftAvailPercent != null) rest.push(`양중가능 ${v.liftAvailPercent}%`)
  return { rating, rest, empty: rating == null && rest.length === 0 }
}

/** 서버·네트워크 오류 → 문구. 서버 message 원문은 쓰지 않음 */
export function settingsErrorMessage(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { code?: string } } } | null
  const res = e?.response
  if (!res) return ORDER_MESSAGE.settings.network
  if (res.data?.code === 'INVALID_LEAD_DAYS') return ORDER_MESSAGE.settings.invalidLeadDays
  if (res.status === 403) return ORDER_MESSAGE.settings.forbidden
  if (res.status != null && res.status >= 500) return ORDER_MESSAGE.settings.server
  return ORDER_MESSAGE.settings.unknown
}
